import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import Heading from './heading';
import Text from './text';
import { PhCaretRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type { SemanticIntent } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// NavCard – unified navigation card with multiple layout variants
// ---------------------------------------------------------------------------

/**
 * Layout variant for the navigation card
 * - `vertical` — Icon centered on top, title below (quick action grid style)
 * - `horizontal` — Icon left, title/subtitle center, caret right (action row style)
 * - `gradient` — Vertical with gradient background (tool card style)
 */
export type NavCardLayout = 'vertical' | 'horizontal' | 'gradient';

/**
 * Semantic intent for the navigation card's icon tint + hover treatment.
 * Replaces the old open decorative `@color` API (violet / indigo / teal /
 * emerald / blue / amber / pink / green / rose). The decorative palette
 * collapses onto the 6 canonical TIO intents via the `.app-nav-card--*`
 * recipe modifiers in styles.css.
 */
export type NavCardIntent = SemanticIntent;

interface NavCardSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Optional query params to pass to LinkTo */
    query?: Record<string, string | number | boolean | null | undefined>;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Optional subtitle text */
    subtitle?: string;
    /** Layout variant (default: 'vertical') */
    layout?: NavCardLayout;
    /** Semantic intent for the icon tint + hover treatment (default: 'brand') */
    intent?: NavCardIntent;
  };
  Element: HTMLAnchorElement;
}

const INTENT_CLASS: Record<NavCardIntent, string> = {
  brand: 'app-nav-card--brand',
  success: 'app-nav-card--success',
  info: 'app-nav-card--info',
  warning: 'app-nav-card--warning',
  danger: 'app-nav-card--danger',
  neutral: 'app-nav-card--neutral',
};

/**
 * NavCard - A unified navigation card with layout variants
 *
 * Consolidates QuickActionLink (vertical), ActionLinkCard (horizontal),
 * and ToolCard (gradient) into a single component with a `@layout` arg.
 *
 * @example Vertical (quick action grid)
 * ```gts
 * import NavCard from 'tio-reimagined/components/nav-card';
 *
 * <NavCard @route="authenticated.explore" @icon={{PhCompass}} @title="Explore" @subtitle="150+ paths" @intent="brand" />
 * ```
 *
 * @example Horizontal (action row)
 * ```gts
 * <NavCard @layout="horizontal" @route="authenticated.tas" @icon={{PhPlus}} @title="Start Application" @subtitle="Request funding" @intent="info" />
 * ```
 *
 * @example Gradient (tool card)
 * ```gts
 * <NavCard @layout="gradient" @route="authenticated.coaching" @icon={{PhChatCircleDots}} @title="Loan Coaches" @subtitle="Expert guidance" @intent="brand" />
 * ```
 */
export default class NavCard extends Component<NavCardSignature> {
  get layout() {
    return this.args.layout ?? 'vertical';
  }

  get intentClass() {
    return INTENT_CLASS[this.args.intent ?? 'brand'];
  }

  get isVertical() {
    return this.layout === 'vertical';
  }

  get isHorizontal() {
    return this.layout === 'horizontal';
  }

  get isGradient() {
    return this.layout === 'gradient';
  }

  get queryParams() {
    return this.args.query ?? {};
  }

  <template>
    {{#if this.isHorizontal}}
      {{! Horizontal layout – icon left, text center, caret right }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="horizontal"
        @route={{@route}}
        @query={{this.queryParams}}
        class="app-nav-card app-nav-card--surface
          {{this.intentClass}}
          flex items-center gap-4 p-5 group"
        ...attributes
      >
        <div data-test-nav-card-icon class="app-nav-card-icon p-3 rounded-xl">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <div class="flex-1">
          <div data-test-nav-card-title class="app-nav-card-title font-semibold">
            {{@title}}
          </div>
          {{#if @subtitle}}
            <Text
              data-test-nav-card-subtitle
              @variant="inherit"
              class="app-nav-card-subtitle text-sm"
            >{{@subtitle}}</Text>
          {{/if}}
        </div>
        <PhCaretRight data-test-nav-card-caret @size={{20}} class="app-nav-card-caret" />
      </LinkTo>
    {{else if this.isGradient}}
      {{! Gradient layout – vertical with gradient background }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="gradient"
        @route={{@route}}
        @query={{this.queryParams}}
        class="app-nav-card app-nav-card--gradient
          {{this.intentClass}}
          flex flex-col items-center gap-2 p-4 text-center"
        ...attributes
      >
        <div data-test-nav-card-icon class="app-nav-card-icon p-3 rounded-xl">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <span data-test-nav-card-title class="app-nav-card-title text-sm font-medium">
          {{@title}}
        </span>
        {{#if @subtitle}}
          <span
            data-test-nav-card-subtitle
            class="app-nav-card-subtitle text-xs"
          >{{@subtitle}}</span>
        {{/if}}
      </LinkTo>
    {{else}}
      {{! Vertical layout (default) – icon on top, text below }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="vertical"
        @route={{@route}}
        @query={{this.queryParams}}
        class="app-nav-card app-nav-card--surface {{this.intentClass}} p-4 group text-center"
        ...attributes
      >
        <div
          data-test-nav-card-icon
          class="app-nav-card-icon app-nav-card-icon--fill p-3 rounded-lg mx-auto w-fit mb-2"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <Heading
          data-test-nav-card-title
          @level={{3}}
          class="app-nav-card-title text-sm font-semibold"
        >{{@title}}</Heading>
        {{#if @subtitle}}
          <Text
            data-test-nav-card-subtitle
            @variant="inherit"
            class="app-nav-card-subtitle text-xs mt-1 hidden sm:block"
          >{{@subtitle}}</Text>
        {{/if}}
      </LinkTo>
    {{/if}}
  </template>
}

export type { NavCardSignature };
